import React, { useState, useEffect } from 'react';
import { Card, Col, Container, Row, Table, Modal, Button, Form, Dropdown } from 'react-bootstrap';
import BreadCrumb from 'Common/BreadCrumb';
import Swal from 'sweetalert2';
import axios from 'axios';

interface ApplicantData {
  id: number;
  name: string;
  email: string;
  phone: string;
  status: string;
  created_at: string;
  updated_at: string;
}

const Applicant = () => {
  document.title = 'Applicant | CITY SERVICE';

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    status: 'pending',
  });
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState<ApplicantData[]>([]);

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setEditId(null);
    setFormData({ name: '', email: '', phone: '', status: 'pending' });
  };

  const handleEditModal = (applicant: ApplicantData) => {
    setEditMode(true);
    setEditId(applicant.id);
    setFormData({ 
      name: applicant.name, 
      email: applicant.email, 
      phone: applicant.phone, 
      status: applicant.status 
    });
    setShowModal(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  const handleFormSubmit = async () => {
    setIsLoading(true);
    Swal.fire({
      title: editMode ? 'Updating...' : 'Saving...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Authentication token is missing. Please log in again.');
      let response;
      if (editMode && editId) {
        response = await axios.patch(
          `/applicant/${editId}`,
          { ...formData },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        Swal.fire('Success', `Applicant "${response.data.name}" updated successfully!`, 'success');
      } else {
        response = await axios.post(
          '/applicant',
          { ...formData },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        Swal.fire('Success', `Applicant "${response.data.name}" added successfully!`, 'success');
      }
      setShowModal(false);
      setEditMode(false);
      setEditId(null);
      setFormData({ name: '', email: '', phone: '', status: 'pending' });
      fetchApplicant();
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'An unknown error occurred',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchApplicant = async () => {
    setIsLoading(true);
    Swal.fire({
      title: 'Loading...',
      html: 'Please wait while we fetch the data.',
      timerProgressBar: true,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Authentication token is missing. Please log in again.');
      const response = await axios.get('/applicant', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTableData(response.data.data || []);
      Swal.fire({ icon: 'success', title: 'Data Loaded', showConfirmButton: false, timer: 1000 });
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'Failed to fetch applicant',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        try {
          const token = localStorage.getItem('authToken');
          if (!token) throw new Error('Authentication token is missing. Please log in again.');
          await axios.delete(`/applicant/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          Swal.fire('Deleted!', 'The applicant has been deleted.', 'success');
          setTableData((prevData) => prevData.filter((item) => item.id !== id));
        } catch (error) {
          Swal.fire(
            'Error',
            (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'Failed to delete applicant',
            'error'
          );
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="badge bg-warning">Pending</span>;
      case 'reviewed':
        return <span className="badge bg-info">Reviewed</span>;
      case 'accepted':
        return <span className="badge bg-success">Accepted</span>;
      case 'rejected':
        return <span className="badge bg-danger">Rejected</span>;
      default:
        return <span className="badge bg-secondary">Unknown</span>;
    }
  };

  useEffect(() => {
    fetchApplicant();
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb pageTitle="Applicant" title="Applicant" />
          <Row>
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0"></h5>
                    <div className="ms-auto">
                      <button
                        className="btn btn-dark d-flex align-items-center gap-1"
                        onClick={handleShowModal}
                      >
                        <i className="ri-add-line"></i>
                        <span>Create</span>
                      </button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Status</th>
                        <th>Created At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(tableData) && tableData.length > 0 ? (
                        tableData.map((applicant, index) => (
                          <tr key={applicant.id}>
                            <td>{index + 1}</td>
                            <td>{applicant.name}</td>
                            <td>{applicant.email}</td>
                            <td>{applicant.phone}</td>
                            <td>{getStatusBadge(applicant.status)}</td>
                            <td>{new Date(applicant.created_at).toLocaleDateString()}</td>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle variant="dark" id={`action-dropdown-${applicant.id}`} className="btn-sm">
                                  <i className="ri-more-2-fill"></i>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => handleEditModal(applicant)}>
                                    <i className="ri-edit-line me-2"></i> Edit
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleDelete(applicant.id)}>
                                    <i className="ri-delete-bin-line me-2"></i> Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center">
                            No applicant available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
        <Modal show={showModal} onHide={handleCloseModal} backdrop="static" centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editMode ? 'Edit Applicant' : 'Add Applicant'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="name">Name</Form.Label>
                <Form.Control
                  type="text"
                  id="name"
                  placeholder="Enter applicant name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  isInvalid={!formData.name.trim()}
                />
                <Form.Control.Feedback type="invalid">
                  Name is required.
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="email">Email</Form.Label>
                <Form.Control
                  type="email"
                  id="email"
                  placeholder="Enter applicant email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  isInvalid={!formData.email.trim()}
                />
                <Form.Control.Feedback type="invalid">
                  Email is required.
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="phone">Phone</Form.Label>
                <Form.Control
                  type="text"
                  id="phone"
                  placeholder="Enter applicant phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  isInvalid={!formData.phone.trim()}
                />
                <Form.Control.Feedback type="invalid">
                  Phone is required.
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="status">Status</Form.Label>
                <Form.Control
                  as="select"
                  id="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={isLoading}
                >
                  <option value="pending">Pending</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                </Form.Control>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="dark" onClick={handleCloseModal} disabled={isLoading}>
              Close
            </Button>
            <Button variant="dark" onClick={handleFormSubmit} disabled={isLoading}>
              {isLoading ? (editMode ? 'Updating...' : 'Saving...') : (editMode ? 'Update' : 'Save')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default Applicant;
